import { getSelectStyles, getActionSelectStyles } from '../utility/selectStyles';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaPlus, FaSyncAlt, FaTrash, FaServer } from 'react-icons/fa';
import { AppDispatch, RootState } from '../store/store';
import { useToast } from '../components/ToastContext';
import Modal from '../components/Modal';
import { useConfirm } from '../components/ConfirmContext';
import axiosInstance from '../utility/axiosInstance';
import { handleAxiosError } from '../helpers/handleAxiosError';
import { fetchInstances } from '../store/slices/instancesSlice';
import { fetchClusters } from '../store/slices/clustersSlice';
import Select, { SingleValue } from 'react-select';
import {fetchSnapshots, setSnapshotStatus} from "../store/slices/snapshotsSlice";
import {fetchOverviewData} from "../store/slices/overviewSlice";
import {fetchImages} from "../store/slices/imagesSlice";
import { fetchVolumes } from '../store/slices/volumesSlice';
import TrialLock, {useResourceCreationLock} from '../components/TrialLock';
import { formatConsoleDate } from '../utility/formatConsoleDate';
import useStorageEstimate from '../hooks/useStorageEstimate';

const ITEMS_PER_PAGE = 10;
const HOURS_PER_MONTH = 730;

const SnapshotsPage: React.FC = () => {
    const { showToast } = useToast();
    const { confirm } = useConfirm();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const instances = useSelector((state: RootState) => state.instances);
    const snapshots = useSelector((state: RootState) => state.snapshots);
    const clusters = useSelector((state: RootState) => state.clusters);
    const volumes = useSelector((state: RootState) => state.volumes.data);
    const billingForecast = useSelector((state: RootState) => state.billingForecast.forecast);

    const creationLocked = useResourceCreationLock();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showCreateSnapshot, setShowCreateSnapshot] = useState(false);
    const [selectedInstance, setSelectedInstance] = useState<string>('');
    const [snapshotName, setSnapshotName] = useState<string>('');
    const [updateCluster, setUpdateCluster] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);

    const instanceObj = instances.data.find((i: any) => i.id === selectedInstance);
    
    let stackId = instanceObj?.metadata?.['OS::stack_id'] || 
                    instanceObj?.metadata?.['stack_id'] || 
                    instanceObj?.metadata?.['metering.stack'];
    let stackName = instanceObj?.metadata?.['OS::stack_name'] || 
                      instanceObj?.metadata?.['stack_name'] || 
                      instanceObj?.metadata?.['metering.stack_name'];

    // Fallback: try to find by instance name prefix if metadata is missing but it's not a manual instance
    if ((!stackId || !stackName) && instanceObj && instanceObj.metadata?.tag !== 'manual') {
        const clusterMatch = clusters.data.find((c: any) => instanceObj.name?.startsWith(`${c.stack_name}-asg`));
        if (clusterMatch) {
            stackId = clusterMatch.id;
            stackName = clusterMatch.stack_name;
        }
    }

    const hasClusterMetadata = !!(stackId && stackName) || (instanceObj && instanceObj.metadata?.tag !== 'manual');
    const activeInstances = instances.data.filter((inst: any) => inst.status === 'ACTIVE');
    const instanceAttachedVolumeIds = Array.isArray((instanceObj as any)?.['os-extended-volumes:volumes_attached'])
        ? (instanceObj as any)['os-extended-volumes:volumes_attached'].map((attachment: any) => String(attachment.id))
        : [];
    // The snapshot workflow prices the source/root volume capacity. Fetching the same volume list
    // makes the pre-create estimate match the amount the workflow records after image creation.
    const snapshotSourceVolume = instanceAttachedVolumeIds
        .map((volumeId: string) => volumes.find((volume: any) => String(volume.id) === volumeId))
        .find(Boolean)
        || volumes.find((volume: any) => volume.attachments?.some(
            (attachment: any) => String(attachment.server_id) === selectedInstance
        ));
    const snapshotEstimate = useStorageEstimate('snapshot', Number(snapshotSourceVolume?.size) || null);

    useEffect(() => {
        if (!creationLocked && location.state && location.state.instanceId) {
            setSelectedInstance(location.state.instanceId);
            setShowCreateSnapshot(true);
            // Optional: generate a default snapshot name
            const inst = instances.data.find((i: any) => i.id === location.state.instanceId);
            if (inst) {
                setSnapshotName(`${inst.name}-snapshot-${new Date().toISOString().slice(0, 10)}`);
            }
        }
    }, [location.state, instances.data, creationLocked]);

    useEffect(() => {
        if (showCreateSnapshot) dispatch(fetchVolumes());
    }, [dispatch, showCreateSnapshot]);

    const getSelectedInstanceLabel = () => {
        if (!selectedInstance) return null;
        const inst = instances.data.find((i: any) => i.id === selectedInstance);
        return inst ? `${inst.name} (${selectedInstance})` : `Instance ${selectedInstance}`;
    };

    useEffect(() => {
        if (instances.status === 'idle') {
            dispatch(fetchInstances());
        }
        if (snapshots.status === 'idle') {
            dispatch(fetchSnapshots());
        }
        if (clusters.status === 'idle') {
            dispatch(fetchClusters());
        }
    }, [dispatch, instances.status, snapshots.status, clusters.status]);

    useEffect(() => {
        setCurrentPage(0);
    }, [snapshots.snapshots.length]);

    const sortedSnapshots = [...snapshots.snapshots].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const totalSnapshots = sortedSnapshots.length;
    const totalPages = Math.ceil(totalSnapshots / ITEMS_PER_PAGE);
    const paginatedSnapshots = sortedSnapshots.slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE
    );

    // const handleCreateSnapshot = async (e: React.FormEvent) => {
    //     e.preventDefault();
    //     if (!selectedInstance || !snapshotName.trim()) return;
    //
    //     setLoading(true);
    //     try {
    //         const payload = {
    //             instanceId: selectedInstance,
    //             snapshotName: snapshotName.trim(),
    //         };
    //
    //         const response = await axiosInstance.post('/v1/snapshot', payload);
    //
    //         if (response.status === 200) {
    //             showToast('Snapshot created successfully!', 'info');
    //             dispatch(fetchSnapshots());
    //             dispatch(fetchOverviewData());
    //             setShowCreateSnapshot(false);
    //             setSnapshotName('');
    //             setSelectedInstance('');
    //         } else {
    //             throw new Error('Failed to start snapshot');
    //         }
    //     } catch (err) {
    //         console.error('Error creating snapshot:', err);
    //         handleAxiosError(err);
    //         showToast('Failed to create snapshot', 'error');
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const handleCreateSnapshot = async (e: React.FormEvent) => {
        e.preventDefault();
        if (creationLocked || !selectedInstance || !snapshotName.trim()) return;

        // Extract the image name from metadata (fall back to image_id if name isn't there)
        const baseImageName = instanceObj?.metadata?.imageName || instanceObj?.image_id;

        setLoading(true);
        try {
            const payload = {
                instanceId: selectedInstance,
                snapshotName: snapshotName.trim(),
                updateCluster,
                stackId,
                stackName,
                // Add the metadata here so the backend can save it with the snapshot
                metadata: {
                    base_image_name: baseImageName,
                    original_instance_id: selectedInstance
                }
            };

            const response = await axiosInstance.post('/v1/snapshot', payload);

            if (response.status === 200) {
                showToast('Snapshot creation started!', 'info');
                dispatch(fetchSnapshots());
                dispatch(fetchInstances());
                dispatch(fetchOverviewData());
                dispatch(fetchImages());
                setShowCreateSnapshot(false);
                setSnapshotName('');
                setSelectedInstance('');
                
                navigate('/category/compute/instances');
                setUpdateCluster(false);
            } else {
                throw new Error('Failed to start snapshot');
            }
        } catch (err) {
            const message = handleAxiosError(err);
            showToast(message || 'Failed to create snapshot', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSnapshot = async (snapshotId: string, snapshotName: string) => {
        const proceed = await confirm({
            title: 'Delete Snapshot',
            message: `Delete snapshot "${snapshotName}"? It can no longer be used to create a virtual machine after deletion.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;

        dispatch(setSnapshotStatus({ id: snapshotId, status: 'deleting' }));

        try {
            await axiosInstance.delete(`/v1/snapshot/${snapshotId}`);
            showToast(`Snapshot "${snapshotName}" deleted`, 'success');
            dispatch(fetchSnapshots());
        } catch (err) {
            console.error('Delete snapshot error:', err);
            handleAxiosError(err);
            showToast(`Failed to delete snapshot "${snapshotName}"`, 'error');
            dispatch(setSnapshotStatus({ id: snapshotId, status: 'active' }));
        }
    };

    const formatSize = (gb: number | string): string => {
        const num = Number(gb);
        if (!num) return '0 GB';
        return `${num.toFixed(2)} GB`;
    };
    const getCurrentHourlyRate = (resourceId: string): number | null => {
        const item = billingForecast?.active.find((resource) =>
            resource.service === 'snapshot' && String(resource.os_resource_id) === String(resourceId)
        );
        const rate = Number(item?.current_hourly_rate);
        return Number.isFinite(rate) ? rate : null;
    };
    const renderCurrentRate = (resourceId: string) => {
        const rate = getCurrentHourlyRate(resourceId);
        if (rate === null) return null;
        const hourly = rate.toFixed(rate > 0 && rate < 0.01 ? 5 : 2);
        return (
            <small style={{ display: 'block', marginTop: 2, color: 'var(--text-muted)' }} title="Current hourly rate before credits">
                Current: £{hourly}/hr · ~£{(rate * HOURS_PER_MONTH).toFixed(2)}/mo
            </small>
        );
    };

    const renderEmptySnapshotState = () => (
        <div className="resource-empty-state">
            <strong>No snapshots yet</strong>
            <span>Create a reusable image of an active virtual machine for recovery or cloning.</span>
            <span className="resource-empty-state__actions">
                <Link to="/category/compute/instances">View virtual machines</Link>
                <Link to="/category/compute/backups">Protect volume data</Link>
            </span>
        </div>
    );

    return (
        <div className="keypairs-page"> 
            <div className="page-header">
                <h1 className="page-title">Snapshots</h1>
                <p className="page-subtitle">
                    Reusable virtual-machine images for recovery or cloning.
                </p>
                <a href="https://docs.redu.cloud/Guides/snapshots-guide" target="_blank" rel="noopener noreferrer" className="docs-link">
                    Documentation →
                </a>
            </div>
            
            <div className="desktop-view">
                    <div className="table-container">
                        <table className="keypairs-table" aria-label="Snapshots created from virtual machines">
                            <thead hidden={snapshots.status !== 'loading' && paginatedSnapshots.length === 0}>
                            <tr>
                                <th>Snapshot Name</th>
                                <th>Instance</th>
                                <th>Size</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            
                            <tbody>
                            {snapshots.status === 'loading' ? (
                                <tr>
                                    <td colSpan={10} className="loading-state">
                                        <FaSyncAlt className="spin"/> Loading snapshots...
                                    </td>
                                </tr>
                            ) : snapshots.status === 'failed' ? (
                                <tr>
                                    <td colSpan={10} className="error-state">
                                        <p>Error: {snapshots.error || 'Failed to load snapshots'}</p>
                                    </td>
                                </tr>
                            ) : paginatedSnapshots.length > 0 ? (
                                paginatedSnapshots.map((snap: any) => (
                                    <tr key={snap.id}>
                                        <td className="keypair-name">{snap.name}</td>
                                        <td>{snap.instance_name || snap.id}</td>
                                        <td>
                                            {formatSize(snap.size)}
                                            {renderCurrentRate(snap.id)}
                                        </td>
                                        <td>
                                            
                                            {snap.status === 'deleting' || snap.status === 'error_deleting' ? (
                                                <div>
                                                    <div className="deleting-progress"></div>
                                                    <div className="status-label" style={{color: '#ef4444'}}>DELETING
                                                    </div>
                                                </div>
                                            ) : snap.status === 'creating' || snap.status === 'downloading' ? (
                                                <div>
                                                    <div className="creating-progress"></div>
                                                    <div className="status-label" style={{color: 'forestgreen'}}>CREATING
                                                    </div>
                                                </div>
                                            ) : (
                                                <span
                                                    className={snap.status === 'active' ? 'text-success' : 'text-warning'}>
              {snap.status}
            </span>
                                            )}
                                        </td>
                                        <td>{formatConsoleDate(snap.created_at)}</td>
                                        <td>
                                            <Select
                                                options={[
                                                    {value: 'delete', label: 'Delete Snapshot'}
                                                ]}
                                                onChange={(option) => {
                                                    if (option?.value === 'delete') {
                                                        handleDeleteSnapshot(snap.id, snap.name);
                                                    }
                                                }}
                                                placeholder="Actions"
                                                isSearchable={false}
                                                aria-label={`Actions for snapshot ${snap.name}`}
                                                menuPortalTarget={document.body}
                                                styles={getActionSelectStyles()}
                                            />
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6} className="empty-state">
                                        {renderEmptySnapshotState()}
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                    disabled={currentPage === 0}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >← Prev</button>
                                <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                    {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalSnapshots)} of {totalSnapshots}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                    disabled={currentPage >= totalPages - 1}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >Next →</button>
                            </div>
                        )}
                    </div>
            </div>

            
            <div className="mobile-view">
                {loading && !snapshots.snapshots.length ? (
                    <div className="loading-state">
                        <FaSyncAlt className="spin"/>
                        <p>Loading snapshots...</p>
                    </div>
                ) : (
                    <div className="cards-container">
                        {paginatedSnapshots.length > 0 ? (
                            paginatedSnapshots.map((snap: any) => (
                                <div key={snap.id} className="keypair-card">
                                    <div className="card-header">
                                        <h3 className="card-title">{snap.name}</h3>
                                        <Select
                                            options={[
                                                {value: 'delete', label: 'Delete Snapshot'}
                                            ]}
                                            onChange={(option) => {
                                                if (option?.value === 'delete') {
                                                    handleDeleteSnapshot(snap.id, snap.name);
                                                }
                                            }}
                                            placeholder="Actions"
                                            isSearchable={false}
                                            aria-label={`Actions for snapshot ${snap.name}`}
                                            menuPortalTarget={document.body}
                                            styles={getActionSelectStyles()}
                                        />
                                    </div>
                                    <div className="card-content">
                                        <div className="fingerprint">
                                            <span className="label">Instance:</span>
                                            <span className="value">{snap.instance_name || snap.id}</span>
                                        </div>
                                        <div className="fingerprint">
                                            <span className="label">Size:</span>
                                            <span className="value">
                                                {formatSize(snap.size)}
                                                {renderCurrentRate(snap.id)}
                                            </span>
                                        </div>
                                        
                                        <div className="fingerprint">
                                            <span className="label">Status:</span>
                                            <span className="value">
              {snap.status === 'deleting' || snap.status === 'error_deleting' ? (
                  <div>
                      <div className="deleting-progress"></div>
                      <div className="status-label" style={{color: '#ef4444'}}>DELETING</div>
                  </div>
              ) : snap.status === 'creating' || snap.status === 'downloading' ? (
                  <div>
                      <div className="creating-progress"></div>
                      <div className="status-label" style={{color: 'forestgreen'}}>CREATING</div>
                  </div>
              ) : (
                  <span className={snap.status === 'active' ? 'text-success' : 'text-warning'}>
                  {snap.status}
                </span>
              )}
            </span>
                                        </div>
                                        <div className="fingerprint">
                                            <span className="label">Created:</span>
                                            <span
                                                className="value">{formatConsoleDate(snap.created_at)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                {renderEmptySnapshotState()}
                            </div>
                        )}
                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px' }}>
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                    disabled={currentPage === 0}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >← Prev</button>
                                <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                    {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalSnapshots)} of {totalSnapshots}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                    disabled={currentPage >= totalPages - 1}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >Next →</button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            
            <TrialLock>
                <button
                    className="create-button"
                    onClick={() => setShowCreateSnapshot(true)}
                    title="Create New Snapshot"
                >
                    <FaServer/>
                    <span>Create Snapshot</span>
                </button>
            </TrialLock>

            
            <Modal
                isOpen={showCreateSnapshot && !creationLocked}
                onClose={() => {
                    setShowCreateSnapshot(false);
                    setSnapshotName('');
                    setSelectedInstance('');
                    setUpdateCluster(false);
                }}
                title="Create Snapshot"
            >
                <form onSubmit={handleCreateSnapshot} className="create-form">
                    <div className="form-group">
                        <label htmlFor="snapshot-instance">Instance</label>
                        <Select
                            inputId="snapshot-instance"
                            options={
                                activeInstances.map((inst: any) => ({
                                        value: inst.id,
                                        label: `${inst.name} (${inst.id})`,
                                    }))
                            }
                            value={selectedInstance ? {
                                value: selectedInstance,
                                label: getSelectedInstanceLabel()!
                            } : null}

                            onChange={(option: SingleValue<{ value: string; label: string }>) => {
                                setSelectedInstance(option?.value || '');
                                // Reset updateCluster if the new instance is manual
                                const inst = instances.data.find((i: any) => i.id === option?.value);
                                if (inst?.metadata?.tag === 'manual') {
                                    setUpdateCluster(false);
                                }
                            }}
                            placeholder="Select an instance"
                            isSearchable={true}
                            menuPortalTarget={document.body}
                            styles={getActionSelectStyles()}
                            required
                        />
                        {activeInstances.length === 0 && (
                            <p className="help-text">
                                Start an active virtual machine before creating a snapshot.
                            </p>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="snapshot-name">Snapshot Name</label>
                        <input
                            id="snapshot-name"
                            type="text"
                            value={snapshotName}
                            onChange={(e) => setSnapshotName(e.target.value)}
                            required
                            placeholder="e.g., backup-april-2025"
                            autoFocus
                        />
                    </div>

                    <p className="help-text">
                        This creates a reusable image of the selected virtual machine, not a separate volume backup. It is retained until you delete it and billed for its stored size.{' '}
                        <a
                            href="https://docs.redu.cloud/Guides/snapshots-guide"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Learn more
                        </a>
                    </p>
                    <p className="help-text">
                        Snapshot storage is billed hourly by the root volume&apos;s stored GB for as long as it is retained.
                        {snapshotSourceVolume && <> The selected virtual machine&apos;s source volume is <strong>{snapshotSourceVolume.size} GB</strong>.</>}
                        {selectedInstance && !snapshotSourceVolume && <> Loading its source-volume capacity…</>}
                        {snapshotEstimate.status === 'loading' && <> Calculating the estimate…</>}
                        {snapshotEstimate.status === 'ready' && <>
                            {' '}Estimated at <strong>£{snapshotEstimate.estimate.hourly_total.toFixed(5)}/hr · ~£{snapshotEstimate.estimate.monthly_total.toFixed(2)}/mo</strong>
                            {' '}for {snapshotEstimate.estimate.size_gb} GB at £{snapshotEstimate.estimate.unit_hourly_rate.toFixed(5)}/GB/hr, before credits.
                        </>}
                        {snapshotEstimate.status === 'unavailable' && <> Snapshot pricing is not configured yet. <Link to="/category/user/billing">Billing</Link> remains the authoritative record for rates, usage, and credits.</>}
                    </p>

                    {hasClusterMetadata && (
                        <div className="form-group" style={{ marginTop: '20px' }}>
                            <label htmlFor="snapshot-update-cluster" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                                <input
                                    id="snapshot-update-cluster"
                                    type="checkbox"
                                    checked={updateCluster}
                                    onChange={(e) => setUpdateCluster(e.target.checked)}
                                    style={{ width: 'auto', marginBottom: 0 }}
                                />
                                <span style={{ fontWeight: 'bold' }}>Use this image for the associated cluster</span>
                            </label>
                            <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                                When the image is ready, the cluster updates to use it and replaces members one at a time. Choose this only after the virtual machine has the startup configuration you want every member to use.
                            </small>
                        </div>
                    )}

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={() => {
                                setShowCreateSnapshot(false);
                                setSnapshotName('');
                                setSelectedInstance('');
                                setUpdateCluster(false);
                            }}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button disabled={loading || !selectedInstance || !snapshotName.trim()} className="submit-button" type="submit">
                            {loading ? 'Creating...' : 'Create Snapshot'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default SnapshotsPage;
